import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useContentProtection } from '../../hooks/useContentProtection';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import {
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Home,
  X,
  Search,
  BookMarked,
  Bookmark,
  BookmarkCheck,
  Menu,
  Highlighter,
  StickyNote,
  GraduationCap,
  Trash2,
  Edit3,
  Save,
  PenLine
} from 'lucide-react';
import DOMPurify from 'dompurify';

const ARCPlaybookReader = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const contentRef = useRef(null);
  
  // Content protection - conditionally applied based on study mode
  const [studyMode, setStudyMode] = useState(false);
  useContentProtection(!studyMode); // Disable protection in study mode
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [playbook, setPlaybook] = useState(null);
  const [sections, setSections] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Study features state
  const [bookmarks, setBookmarks] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [notes, setNotes] = useState([]);
  const [currentNote, setCurrentNote] = useState('');
  const [notesPanelOpen, setNotesPanelOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [selectedText, setSelectedText] = useState(null);
  const [highlightToolbar, setHighlightToolbar] = useState(null);
  const [showBookmarksOnly, setShowBookmarksOnly] = useState(false);

  const fetchPlaybook = useCallback(async () => {
    try {
      setLoading(true);
      const [playbookRes, studyDataRes] = await Promise.all([
        api.get('/arc/playbook'),
        api.get('/arc/playbook/study-data')
      ]);
      
      const data = playbookRes.data;
      setPlaybook(data.playbook);
      setSections(data.playbook.sections || []);
      
      // Load study data
      setBookmarks(studyDataRes.data.bookmarks || []);
      setHighlights(studyDataRes.data.highlights || []);
      setNotes(studyDataRes.data.notes || []);
      
      // Check for section param in URL or use last read position
      const sectionParam = searchParams.get('section');
      if (sectionParam) {
        const idx = data.playbook.sections.findIndex(s => s.id === sectionParam);
        if (idx >= 0) setCurrentSectionIndex(idx);
      } else if (data.last_section_id) {
        const idx = data.playbook.sections.findIndex(s => s.id === data.last_section_id);
        if (idx >= 0) setCurrentSectionIndex(idx);
      }
    } catch (error) {
      console.error('Failed to fetch playbook:', error);
      if (error.response?.status === 403) {
        toast({
          title: 'Not Enrolled',
          description: 'Please enroll in ARC first to access the playbook.',
          variant: 'destructive'
        });
        navigate('/arc');
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load playbook content',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [api, navigate, toast, searchParams]);

  useEffect(() => {
    fetchPlaybook();
  }, [fetchPlaybook]);

  // Save progress when section changes
  useEffect(() => {
    if (sections.length > 0 && !loading) {
      const currentSection = sections[currentSectionIndex];
      if (currentSection) {
        setSearchParams({ section: currentSection.id });
        api.post(`/arc/playbook/progress?section_id=${currentSection.id}`).catch(() => {});
        
        // Load note for current section
        const sectionNote = notes.find(n => n.section_id === currentSection.id);
        setCurrentNote(sectionNote?.note_text || '');
        setEditingNote(false);
      }
    }
  }, [currentSectionIndex, sections, loading, setSearchParams, api, notes]);

  // Scroll to top when section changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
    setHighlightToolbar(null);
    setSelectedText(null);
  }, [currentSectionIndex]);

  // Handle text selection for highlighting
  useEffect(() => {
    if (!studyMode) return;
    
    const handleSelection = () => {
      const selection = window.getSelection();
      const text = selection.toString().trim();
      
      if (text.length > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        
        setSelectedText(text);
        setHighlightToolbar({
          top: rect.top - 50,
          left: rect.left + (rect.width / 2) - 60
        });
      } else {
        setHighlightToolbar(null);
        setSelectedText(null);
      }
    };
    
    document.addEventListener('mouseup', handleSelection);
    return () => document.removeEventListener('mouseup', handleSelection);
  }, [studyMode]);

  const goToSection = (index) => {
    setCurrentSectionIndex(index);
    setSidebarOpen(false);
  };

  const goToPrevSection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
    }
  };

  const goToNextSection = () => {
    if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
    }
  };

  // Bookmark functions
  const toggleBookmark = async () => {
    const currentSection = sections[currentSectionIndex];
    if (!currentSection) return;
    
    try {
      const res = await api.post(`/arc/playbook/bookmark/${currentSection.id}`);
      setBookmarks(res.data.bookmarks);
      toast({
        title: res.data.action === 'added' ? 'Bookmarked!' : 'Bookmark removed',
        description: res.data.action === 'added' 
          ? `Section ${currentSection.number} saved to bookmarks` 
          : `Section ${currentSection.number} removed from bookmarks`
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update bookmark', variant: 'destructive' });
    }
  };

  const isCurrentBookmarked = bookmarks.includes(sections[currentSectionIndex]?.id);

  // Highlight functions
  const addHighlight = async (color = 'yellow') => {
    if (!selectedText || !studyMode) return;
    
    const currentSection = sections[currentSectionIndex];
    try {
      const res = await api.post('/arc/playbook/highlight', {
        section_id: currentSection.id,
        text: selectedText,
        start_offset: 0, // Simplified - we'll use text matching
        end_offset: selectedText.length,
        color
      });
      
      setHighlights(prev => [...prev, res.data.highlight]);
      setHighlightToolbar(null);
      setSelectedText(null);
      window.getSelection().removeAllRanges();
      
      toast({ title: 'Highlighted!', description: 'Text has been highlighted' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save highlight', variant: 'destructive' });
    }
  };

  const removeHighlight = async (highlightId) => {
    try {
      await api.delete(`/arc/playbook/highlight/${highlightId}`);
      setHighlights(prev => prev.filter(h => h.id !== highlightId));
      toast({ title: 'Removed', description: 'Highlight removed' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to remove highlight', variant: 'destructive' });
    }
  };

  // Get highlights for current section
  const currentHighlights = highlights.filter(h => h.section_id === sections[currentSectionIndex]?.id);

  // Note functions
  const saveNote = async () => {
    const currentSection = sections[currentSectionIndex];
    if (!currentSection) return;
    
    try {
      const res = await api.post('/arc/playbook/note', {
        section_id: currentSection.id,
        note_text: currentNote
      });
      
      setNotes(prev => {
        const filtered = prev.filter(n => n.section_id !== currentSection.id);
        return [...filtered, res.data.note];
      });
      setEditingNote(false);
      toast({ title: 'Note saved!', description: 'Your note has been saved' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save note', variant: 'destructive' });
    }
  };

  const deleteNote = async () => {
    const currentSection = sections[currentSectionIndex];
    if (!currentSection) return;
    
    try {
      await api.delete(`/arc/playbook/note/${currentSection.id}`);
      setNotes(prev => prev.filter(n => n.section_id !== currentSection.id));
      setCurrentNote('');
      setEditingNote(false);
      toast({ title: 'Deleted', description: 'Note removed' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete note', variant: 'destructive' });
    }
  };

  const currentSectionHasNote = notes.some(n => n.section_id === sections[currentSectionIndex]?.id);

  // Filter sections for search and bookmarks
  const filteredSections = sections.filter(section => {
    const matchesSearch = searchQuery === '' || 
      section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesBookmark = !showBookmarksOnly || bookmarks.includes(section.id);
    return matchesSearch && matchesBookmark;
  });

  // Highlight text in content
  const renderContentWithHighlights = (text) => {
    if (!currentHighlights.length) return text;
    
    let result = text;
    currentHighlights.forEach(highlight => {
      const escapedText = highlight.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedText})`, 'gi');
      result = result.replace(regex, `<mark class="bg-yellow-200 dark:bg-yellow-900/50 px-0.5 rounded cursor-pointer" data-highlight-id="${highlight.id}">$1</mark>`);
    });
    
    return result;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-4 gap-6">
            <Skeleton className="h-[70vh] col-span-1" />
            <Skeleton className="h-[70vh] col-span-3" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!playbook) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 text-center max-w-md">
            <BookMarked className="h-12 w-12 mx-auto mb-4 text-amber-500" />
            <h2 className="text-xl font-bold mb-2">Playbook Not Available</h2>
            <p className="text-muted-foreground mb-4">
              Please enroll in the Advanced Recruitment Certification to access the full playbook.
            </p>
            <Button onClick={() => navigate('/arc')}>Go to ARC Dashboard</Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const currentSection = sections[currentSectionIndex];
  const progressPercentage = ((currentSectionIndex + 1) / sections.length) * 100;

  return (
    <MainLayout>
      {/* Top notice bar */}
      <div className={`border-b px-4 py-2 ${
        studyMode 
          ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' 
          : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800'
      }`}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <p className={`text-xs ${studyMode ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>
            {studyMode 
              ? '📚 Study Mode: Select text to highlight. Copy/paste still disabled for protection.' 
              : 'This content is protected. Enable Study Mode to highlight and take notes.'
            }
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">Study Mode</span>
            <Switch 
              checked={studyMode} 
              onCheckedChange={setStudyMode}
              data-testid="study-mode-toggle"
            />
          </div>
        </div>
      </div>

      {/* Highlight toolbar (appears when text selected in study mode) */}
      {highlightToolbar && studyMode && (
        <div 
          className="fixed z-50 bg-white dark:bg-slate-800 shadow-lg rounded-lg p-2 flex gap-1 border"
          style={{ top: highlightToolbar.top, left: highlightToolbar.left }}
        >
          <Button size="sm" variant="ghost" onClick={() => addHighlight('yellow')} className="h-8 w-8 p-0">
            <div className="w-5 h-5 rounded bg-yellow-300" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => addHighlight('green')} className="h-8 w-8 p-0">
            <div className="w-5 h-5 rounded bg-green-300" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => addHighlight('blue')} className="h-8 w-8 p-0">
            <div className="w-5 h-5 rounded bg-blue-300" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => addHighlight('pink')} className="h-8 w-8 p-0">
            <div className="w-5 h-5 rounded bg-pink-300" />
          </Button>
        </div>
      )}

      <div 
        className={`flex h-[calc(100vh-8rem)] ${!studyMode ? 'select-none' : ''}`}
        style={!studyMode ? { userSelect: 'none', WebkitUserSelect: 'none' } : {}}
        onContextMenu={(e) => e.preventDefault()}
        onCopy={(e) => e.preventDefault()}
      >
        {/* Mobile Sidebar Toggle */}
        <Button
          variant="outline"
          size="sm"
          className="fixed bottom-4 left-4 z-50 lg:hidden shadow-lg"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>

        {/* Sidebar */}
        <div className={`
          fixed lg:relative inset-y-0 left-0 z-40 w-80 bg-background border-r
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="flex flex-col h-full">
            {/* Sidebar Header */}
            <div className="p-4 border-b bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
              <div className="flex items-center justify-between mb-3">
                <Button variant="ghost" size="sm" onClick={() => navigate('/arc')}>
                  <Home className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="lg:hidden"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <h2 className="font-bold text-lg text-amber-900 dark:text-amber-100 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                {playbook.title}
              </h2>
              <p className="text-xs text-muted-foreground mt-1">{playbook.subtitle}</p>
            </div>

            {/* Search & Filter */}
            <div className="p-3 border-b space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search sections..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowBookmarksOnly(!showBookmarksOnly)}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                    showBookmarksOnly 
                      ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300' 
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <BookmarkCheck className="h-3 w-3" />
                  Bookmarks ({bookmarks.length})
                </button>
                <button
                  onClick={() => setNotesPanelOpen(!notesPanelOpen)}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                    notesPanelOpen 
                      ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300' 
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <StickyNote className="h-3 w-3" />
                  Notes ({notes.length})
                </button>
              </div>
            </div>

            {/* Sections List */}
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {filteredSections.map((section) => {
                  const actualIdx = sections.findIndex(s => s.id === section.id);
                  const isActive = actualIdx === currentSectionIndex;
                  const isBookmarked = bookmarks.includes(section.id);
                  const hasNote = notes.some(n => n.section_id === section.id);
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => goToSection(actualIdx)}
                      className={`w-full text-left p-3 rounded-lg text-sm transition-all ${
                        isActive
                          ? 'bg-amber-100 dark:bg-amber-900/50 border-l-4 border-amber-500'
                          : 'hover:bg-muted'
                      }`}
                      data-testid={`playbook-section-${section.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                          isActive 
                            ? 'bg-amber-500 text-white' 
                            : 'bg-muted-foreground/20 text-muted-foreground'
                        }`}>
                          {section.number}
                        </span>
                        <span className={`font-medium truncate flex-1 ${isActive ? 'text-amber-900 dark:text-amber-100' : ''}`}>
                          {section.title}
                        </span>
                        <div className="flex gap-1">
                          {isBookmarked && <Bookmark className="h-3 w-3 text-amber-500 fill-amber-500" />}
                          {hasNote && <StickyNote className="h-3 w-3 text-emerald-500" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Progress Footer */}
            <div className="p-4 border-t bg-muted/30">
              <div className="flex justify-between text-xs text-muted-foreground mb-2">
                <span>Reading Progress</span>
                <span>{currentSectionIndex + 1} of {sections.length}</span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top Navigation Bar */}
          <div className="flex items-center justify-between p-4 border-b bg-background">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300">
                Section {currentSectionIndex + 1} of {sections.length}
              </Badge>
              <h1 className="text-lg font-bold hidden sm:block truncate max-w-md">
                {currentSection?.title}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {/* Study Tools */}
              <Button
                variant={isCurrentBookmarked ? "default" : "outline"}
                size="sm"
                onClick={toggleBookmark}
                className={isCurrentBookmarked ? 'bg-amber-500 hover:bg-amber-600' : ''}
                data-testid="bookmark-btn"
              >
                {isCurrentBookmarked ? (
                  <BookmarkCheck className="h-4 w-4" />
                ) : (
                  <Bookmark className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant={notesPanelOpen ? "default" : "outline"}
                size="sm"
                onClick={() => setNotesPanelOpen(!notesPanelOpen)}
                className={notesPanelOpen ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                data-testid="notes-btn"
              >
                <StickyNote className="h-4 w-4" />
              </Button>
              
              <div className="w-px h-6 bg-border mx-1" />
              
              {/* Navigation */}
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevSection}
                disabled={currentSectionIndex === 0}
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Previous</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextSection}
                disabled={currentSectionIndex === sections.length - 1}
              >
                <span className="hidden sm:inline mr-1">Next</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content with optional Notes Panel */}
          <div className="flex-1 flex overflow-hidden">
            {/* Main Content */}
            <ScrollArea className="flex-1" ref={contentRef}>
              <div className="max-w-4xl mx-auto p-6 lg:p-10">
                {/* Section Header */}
                <div className="mb-8 pb-6 border-b">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-white text-xl font-bold">
                      {currentSection?.number}
                    </span>
                    <div className="flex-1">
                      <h1 className="text-2xl lg:text-3xl font-bold text-amber-900 dark:text-amber-100">
                        {currentSection?.title}
                      </h1>
                    </div>
                  </div>
                  
                  {/* Highlights summary */}
                  {currentHighlights.length > 0 && (
                    <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300 flex items-center gap-1">
                          <Highlighter className="h-3 w-3" />
                          {currentHighlights.length} highlight{currentHighlights.length !== 1 ? 's' : ''} in this section
                        </span>
                        <button 
                          onClick={() => currentHighlights.forEach(h => removeHighlight(h.id))}
                          className="text-xs text-yellow-600 hover:text-yellow-800"
                        >
                          Clear all
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Section Content */}
                <div 
                  className={`prose prose-lg dark:prose-invert max-w-none ${!studyMode ? 'select-none' : ''}`}
                  style={!studyMode ? { userSelect: 'none' } : {}}
                >
                  {currentSection?.content.split('\n\n').map((paragraph, i) => {
                    const isHeading = paragraph.length < 50 && (
                      paragraph.endsWith(':') || 
                      paragraph.match(/^[A-Z][a-zA-Z\s]+$/)
                    );
                    const isBullet = paragraph.trim().startsWith('•') || paragraph.trim().startsWith('-');
                    
                    const processedText = DOMPurify.sanitize(renderContentWithHighlights(paragraph));
                    
                    if (isHeading) {
                      return (
                        <h3 
                          key={`heading-${i}`} 
                          className="text-lg font-semibold mt-8 mb-4 text-amber-800 dark:text-amber-200"
                          dangerouslySetInnerHTML={{ __html: processedText.replace(/:$/, '') }}
                        />
                      );
                    }
                    
                    if (isBullet) {
                      return (
                        <p 
                          key={`bullet-${i}`} 
                          className="mb-2 pl-4 border-l-2 border-amber-300"
                          dangerouslySetInnerHTML={{ __html: processedText.replace(/^[•-]\s*/, '') }}
                        />
                      );
                    }
                    
                    return (
                      <p 
                        key={`para-${i}`} 
                        className="mb-4 leading-relaxed text-gray-800 dark:text-gray-200"
                        dangerouslySetInnerHTML={{ __html: processedText }}
                      />
                    );
                  })}
                </div>

                {/* Bottom Navigation */}
                <div className="flex items-center justify-between mt-12 pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={goToPrevSection}
                    disabled={currentSectionIndex === 0}
                    className="flex items-center gap-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <div className="text-left hidden sm:block">
                      <p className="text-xs text-muted-foreground">Previous</p>
                      <p className="text-sm font-medium truncate max-w-[150px]">
                        {sections[currentSectionIndex - 1]?.title || 'Start'}
                      </p>
                    </div>
                  </Button>

                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      Section {currentSectionIndex + 1} of {sections.length}
                    </p>
                  </div>

                  <Button
                    variant={currentSectionIndex === sections.length - 1 ? 'default' : 'outline'}
                    onClick={currentSectionIndex === sections.length - 1 ? () => navigate('/arc') : goToNextSection}
                    className={`flex items-center gap-2 ${
                      currentSectionIndex === sections.length - 1 
                        ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700' 
                        : ''
                    }`}
                  >
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-muted-foreground">
                        {currentSectionIndex === sections.length - 1 ? 'Finished' : 'Next'}
                      </p>
                      <p className="text-sm font-medium truncate max-w-[150px]">
                        {currentSectionIndex === sections.length - 1 
                          ? 'Back to ARC' 
                          : sections[currentSectionIndex + 1]?.title}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </ScrollArea>

            {/* Notes Panel */}
            {notesPanelOpen && (
              <div className="w-80 border-l bg-emerald-50/50 dark:bg-emerald-950/20 flex flex-col">
                <div className="p-4 border-b bg-emerald-100/50 dark:bg-emerald-900/30">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold flex items-center gap-2 text-emerald-800 dark:text-emerald-200">
                      <StickyNote className="h-4 w-4" />
                      Notes
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => setNotesPanelOpen(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                    Section {currentSection?.number}: {currentSection?.title}
                  </p>
                </div>
                
                <div className="flex-1 p-4 overflow-auto">
                  {editingNote ? (
                    <div className="space-y-3">
                      <Textarea
                        value={currentNote}
                        onChange={(e) => setCurrentNote(e.target.value)}
                        placeholder="Write your notes here..."
                        className="min-h-[200px] text-sm"
                        data-testid="note-textarea"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveNote} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                          <Save className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingNote(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : currentNote ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border text-sm whitespace-pre-wrap">
                        {currentNote}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditingNote(true)} className="flex-1">
                          <Edit3 className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={deleteNote} className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <PenLine className="h-8 w-8 mx-auto text-emerald-400 mb-3" />
                      <p className="text-sm text-muted-foreground mb-4">No notes for this section yet</p>
                      <Button size="sm" onClick={() => setEditingNote(true)} className="bg-emerald-600 hover:bg-emerald-700">
                        <Edit3 className="h-4 w-4 mr-1" />
                        Add Note
                      </Button>
                    </div>
                  )}
                </div>

                {/* All notes list */}
                {notes.length > 0 && (
                  <div className="border-t p-4">
                    <p className="text-xs font-medium text-muted-foreground mb-2">All your notes ({notes.length})</p>
                    <div className="space-y-2 max-h-40 overflow-auto">
                      {notes.map(note => {
                        const section = sections.find(s => s.id === note.section_id);
                        return (
                          <button
                            key={note.section_id}
                            onClick={() => {
                              const idx = sections.findIndex(s => s.id === note.section_id);
                              if (idx >= 0) goToSection(idx);
                            }}
                            className="w-full text-left p-2 text-xs rounded bg-white dark:bg-slate-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                          >
                            <p className="font-medium truncate">Section {section?.number}: {section?.title}</p>
                            <p className="text-muted-foreground truncate">{note.note_text}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ARCPlaybookReader;
